import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Chip } from 'src/components/Chip';
import { Container } from 'src/components/Container';
import { Divider } from 'src/components/Divider';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Skeleton } from 'src/components/Skeleton';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { FranchiseFeePayersQueryKey, fetchFranchiseFeePayers } from 'src/models/hq/franchise-fee-payer/franchise-fee-payer';
import {
  fetchFranchiseFeePayout,
  getPayoutStatusChipProps,
} from 'src/models/hq/franchise-fee-payout';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { formatMonthYear } from 'src/utils/dateFormatter';
import { formatCurrency } from 'src/utils/numberFormatter';

import { PayerDetails } from './PayerDetails';
import { PayoutAdjustmentsList } from './PayoutAdjustmentsList';
import { PayoutFinancialBreakdown } from './PayoutFinancialBreakdown';
import { PayoutInitiateDialog } from './PayoutInitiateDialog';

export default function PayoutDetailPage() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'franchiseFees.payouts',
  });
  const { id } = useParams<{ id: string }>();
  const [initiateDialogOpen, setInitiateDialogOpen] = useState(false);

  const {
    data: payout,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['franchise-fee-payout', id],
    queryFn: () => fetchFranchiseFeePayout(id!),
    enabled: !!id,
  });
  const payoutPeriod = payout?.payoutPeriodStart
    ? formatMonthYear(payout.payoutPeriodStart, 'UTC')
    : 'Unknown Period';
  const payerPlatformName = payout?.payerPlatformName || 'Unknown Platform';
  const { color, label } = payout
    ? getPayoutStatusChipProps(payout.payoutStatus)
    : { color: 'default', label: '' };

  const title = isLoading
    ? t('Payout')
    : isError || !payout
      ? t('Payout Not Found')
      : `${payoutPeriod} - ${payerPlatformName}`;

  const handleOpenInitiateDialog = () => {
    setInitiateDialogOpen(true);
  };

  const handleCloseInitiateDialog = () => {
    setInitiateDialogOpen(false);
  };

  const handleInitiatePayout = () => {
    // TODO: Implement payout initiation logic
    setInitiateDialogOpen(false);
  };

  // Fetch all payers if payer is not set on the payout
  const { data: payersData, isLoading: isLoadingPayers } = useQuery({
    queryKey: [FranchiseFeePayersQueryKey, { fetchAll: true }],
    queryFn: () =>
      fetchFranchiseFeePayers({
        page: 1,
        perPage: 1000, // Fetch all payers
      }),
    enabled: !!payout,
  });

  const payerPlatformId = payout?.payerPlatformId;


  const showCanStartPayoutStatus = payout?.payoutStatus !== 'in_progress' && payout?.payoutStatus !== 'completed'
  const buttonText = payout?.payoutStatus === 'failed' ? t('retryPayout') : t('startPayout');
  const loading = isLoadingPayers || isLoading;
  const matchedPayer = payersData?.franchiseFeePayers.find((payer) => {
    if (showCanStartPayoutStatus) {
      return payerPlatformId ? payer.platformIds.includes(payerPlatformId) : false;
    }
    return payer.id === payout?.payerId;
  });

const canStartPayout = (payout?.payoutStatus === 'not_started' || payout?.payoutStatus === 'failed') && payout?.amountOwed > 0 && matchedPayer?.paymentMethod;
  return (
    <>
      <AppFrameConfig
        id="franchise-fee-payout-detail"
        containerMaxWidth="lg"
        title={title}
        backButtonTitle={t('Payouts')}
        backButtonLinkTo="/franchise-fees/payouts"
      />
      {isLoading && (
        <Container>
          <Card sx={{ p: 4 }}>
            <Skeleton variant="text" width="60%" height={32} sx={{ mb: 3 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="90%" height={20} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="70%" height={20} />
          </Card>
        </Container>
      )}
      {(isError || !payout) && !isLoading && (
        <Container>
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="error" gutterBottom>
              {t('Payout Not Found')}
            </Typography>
            <Typography color="text.secondary">
              {t('The requested payout could not be found.')}
            </Typography>
          </Card>
        </Container>
      )}
      {payout && !isLoading && !isError && (
        <Container>
          {/* Status Header */}
          <Box sx={{ mb: 2 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" component="h1">
                {t('payoutDetails')}
              </Typography>
              <Chip color={color as any} label={label} size="small" />
            </Stack>
          </Box>

          {/* Errors */}
          {payout.errorMessage && (
            <Card sx={{ p: 2, mb: 2 }}>
              <Box>
              <Typography variant="h6" color="error" gutterBottom>
                  {t('PaymentError')}
                </Typography>
                <Typography variant="body1" color="error">
                  {payout.errorMessage}
                </Typography>
              </Box>
            </Card>
          )}



          <Card sx={{ mb: 4 }}>
            {/* Financial Breakdown */}
            <PayoutFinancialBreakdown payout={payout} />

            <Divider />

            {/* Adjustments */}

            <PayoutAdjustmentsList payout={payout} />

            <Divider />

            {matchedPayer && (
              <>
                <Box sx={{ p: 3 }}>
                  <PayerDetails payer={matchedPayer} isLoading={isLoadingPayers} />
                </Box>
                <Divider />
              </>
            )}

            {/* Amount Owed Summary */}
            <Box sx={{ p: 3 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6" fontWeight={600}>
                  {t('amountOwed')}
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {formatCurrency(payout.amountOwedInDollars || 0)}
                </Typography>
              </Stack>
              {showCanStartPayoutStatus && (
              <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mt: 2 }}>
                <Button
                  disabled={!canStartPayout}
                  variant="contained"
                  color="primary"
                  sx={{ width: '100%' }}
                  onClick={handleOpenInitiateDialog}
                  loading={loading}

                >
                  {buttonText}
                </Button>
              </Stack>
              )}{showCanStartPayoutStatus && (
              !canStartPayout && !loading && (!matchedPayer || !matchedPayer.paymentMethod) && (
                <Stack direction="row" justifyContent="flex-start" alignItems="center" sx={{ mt: 2 }}>
                  <Typography variant="body1" color="error">
                    {!matchedPayer && t('cannotStartPayoutMessage', { payerPlatformName })}
                    {matchedPayer && !matchedPayer.paymentMethod && t('cannotStartPayoutMessageNoPaymentMethod')}
                  </Typography>
                  <IconButton color="error" component={Link} to="/franchise-fees/payers">
                    <Icon icon={faEdit} />
                  </IconButton>
                </Stack>
              )
              )}
            </Box>
          </Card>
        </Container>
      )}
      {/* Payout Initiate Dialog */}
      <PayoutInitiateDialog
        open={initiateDialogOpen}
        onClose={handleCloseInitiateDialog}
        payout={payout || null}
        onInitiatePayout={handleInitiatePayout}
      />
    </>
  );
}
